import { useParams, Navigate, Link } from "react-router-dom";
import { Check } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import PillButton from "../components/common/PillButton";
import CTASection from "../components/common/CTASection";
import { Icon } from "../lib/icon";
import { useContent } from "../context/ContentContext";
import { useStaffing } from "../components/forms/StaffingProvider";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { getService, services } = useContent();
  const service = getService(slug);
  const { open } = useStaffing();
  if (!service) return <Navigate to="/services" replace />;

  const isStaffing = service.slug === "it-staffing";
  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={service.name} description={service.tagline} path={`/services/${slug}`} />
      <PageHero eyebrow="Service" title={service.name} subtitle={service.tagline} image={service.image}>
        {isStaffing ? (
          <PillButton onClick={open} size="lg" testId="service-hero-cta">{service.cta}</PillButton>
        ) : (
          <PillButton to="/contact" size="lg" testId="service-hero-cta">{service.cta}</PillButton>
        )}
      </PageHero>

      <section className="px-6 py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-sky-400">
              <Icon name={service.icon} className="h-7 w-7" />
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white lg:text-3xl">
              The Challenge
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{service.problem}</p>
            <p className="mt-4 text-base leading-relaxed text-slate-400">{service.description}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-ink-800/50 p-8">
              <h3 className="font-heading text-xl font-bold tracking-tight text-white">Capabilities</h3>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {service.capabilities.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                      <Check className="h-3 w-3" />
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5 px-6 py-16">
        <div className="mx-auto max-w-7xl">
          <h3 className="font-heading text-xl font-bold tracking-tight text-white">Explore other services</h3>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="group rounded-2xl border border-white/10 bg-ink-800/40 p-6 transition-colors hover:border-sky-400/40"
              >
                <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-sky-400">
                  <Icon name={o.icon} className="h-5 w-5" />
                </div>
                <div className="font-heading font-semibold text-white group-hover:text-sky-300">{o.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
